/**
 * Spanish copy dictionary.
 *
 * Verbatim from docs/content/website_content_pass_v1.md §6 (6.1–6.11).
 * Same {placeholder} discipline as English — see the note in copy.en.ts.
 *
 * ⚠️ The consent strings under wizard.step5 are §6.6, marked "legally
 * critical, must be accurate" in the source. They are TCPA-operative
 * language: do not paraphrase, shorten, or machine-translate them.
 *
 * `satisfies CopyDict` means a missing key breaks the build rather than
 * silently rendering English to a Spanish-speaking customer.
 */

import type { CopyDict } from './types';

export const es = {
  tagline: {
    header: '{city}, {stateFull} · Desde {foundedYear}',
    footer: '{city}, {state} · Desde {foundedYear}',
  },
  localeToggle: { label: 'Idioma', en: 'EN', es: 'ES' },

  nav: {
    book: 'Reservar',
    barbers: 'Barberos',
    gallery: 'Galería',
    walkIns: 'Sin cita',
    visit: 'Visítanos',
    home: 'Inicio',
    about: 'Nosotros',
    menu: 'Carta',
    services: 'Servicios',
    openMenu: 'Menú',
    closeMenu: 'Cerrar menú',
  },

  meta: {
    localeName: 'Español',
    days: { mon: 'Lun', tue: 'Mar', wed: 'Mié', thu: 'Jue', fri: 'Vie', sat: 'Sábado', sun: 'Domingo' },
    weekdayRange: 'Lun – Vie',
    closed: 'Cerrado',
    hoursLabel: 'Horas',
  },

  bookRedirect: {
    headline: 'Te llevamos a Booksy…',
    body: 'Si no pasa nada en un segundo, usa el botón de abajo — las reservas de {shopName} están en Booksy.',
    cta: 'Reserva en Booksy',
  },

  home: {
    hero: {
      kicker: '{city}, {stateFull} · Desde {foundedYear}',
      boricuaPill: 'Boricua desde {foundedYear} en {city}',
      headline: 'La barbería más reservada de {city}.',
      subhead: {
        lead: 'Siéntate — te guardamos una silla. {barberCount} barberos, una corona. Reserva la hora que te sirva, o pásate — ',
        accent: 'sin cita, sin problema',
        tail: ' — y te escribimos cuando la silla esté lista.',
      },
      intro: {
        line1:
          'Ven y toma asiento — tenemos una silla esperándote. Conseguir un corte fresco nunca ha sido tan fácil.',
        line2: 'Reserva la hora que te convenga o simplemente entra. Sin cita, sin problema',
      },
      est: 'Est. {foundedYear}',
      ctaPrimary: 'Reserva tu silla',
      ctaSecondary: 'Ponte en la fila',
      appointmentDeskLabel: 'Reserva de citas',
      openToday: 'Abierto hoy',
      closedToday: 'Cerrado hoy',
      desk: {
        line1: 'Un plan más afilado',
        line2Prefix: 'para',
        cta: 'Elige una hora',
        today: 'hoy',
        tomorrow: 'mañana',
        closedBooksyCta: 'O reserva en Booksy ahora',
      },
      mobileCard: { label: 'La silla' },
      liveStatus: {
        open: 'Unos {n} minutos ahora mismo',
        meta: '{cutting} sillas cortando, {waiting} personas esperando',
        empty: 'Silla libre ahora mismo',
        closed: 'Abrimos {when}',
        whenToday: 'hoy a las {time}',
        whenTomorrow: 'mañana a las {time}',
        whenDay: 'el {day} a las {time}',
        staffed: 'Listos para ti · {count} en la silla',
        staffedOne: 'Listos para ti · una silla cortando',
        unstaffed: 'Las sillas abren cuando abren las puertas · hoy a las {openTime}',
        closedUntil: 'Nos vemos {when} · abrimos a las {openTime}',
        whenTomorrowBare: 'mañana',
        whenDayBare: 'el {day}',
      },
    },
    shopStory: {
      eyebrow: 'La tienda · The shop',
      headline: 'Barbería con movimiento. Oficio en serio.',
      body:
        'Kenny llegó a {city} con una máquina y un plan. {yearsSinceFounding} años después tiene una silla con su nombre, {barbersBesidesOwner} barberos a su lado, y {combinedReviewTotal} personas que siguen volviendo. Ven a ver por qué.',
      meta: 'Negocio de familia. Boricua. Los sábados casi siempre llenos.',
      imageAlt: 'Kenny cortándole el pelo a un cliente joven en {shopName}',
      mission: {
        heading: 'Nuestra Misión',
        lead: 'En ',
        emph: '{shopName}',
        tail: ', nuestra misión es ofrecer servicios de cuidado personal de alta calidad en un ambiente acogedor, profesional y cómodo. Estamos comprometidos a que cada cliente se sienta seguro y renovado gracias a un gran servicio, atención al detalle y una experiencia personalizada.',
      },
      vision: {
        heading: 'Nuestra Visión',
        p1: 'Nuestra visión es ser una barbería de confianza en ',
        emph1: 'la comunidad de {city}',
        p2: ', reconocida por su calidad, consistencia y un servicio al cliente excepcional. Buscamos crear más que buenos cortes: queremos que ',
        emph2: '{shopName}',
        p3: ' sea un lugar donde cada cliente se sienta bienvenido, valorado y seguro cada vez que cruza nuestras puertas.',
      },
    },
    liveStrip: {
      quiet: 'La barbería está tranquila. Buen momento para reservar.',
      booked: '{name} acaba de reservar con {barber} — {time}',
      checkedIn: '{name} acaba de registrarse como walk-in',
    },
    whyKd: {
      kicker: 'Por qué KD · Why KD',
      headline: 'Barbería llena. Gran oficio. Cero actitud.',
      columnLabels: {
        booking: 'Con cita o sin cita',
        quality: 'Cortes que aguantan',
        location: 'En {streetName}',
      },
      columns: {
        booking:
          'Reserva la hora que quieras, o pásate y agarra la próxima silla libre. Ambas funcionan.',
        quality:
          'Cada barbero del equipo tiene {minBarberRating}+ estrellas en Booksy. No contratamos a nadie que no tenga las reseñas para respaldarlo.',
        location: 'Esquina de {crossStreet}. Parking gratis. El WiFi es {wifiPassword} por si esperas.',
      },
    },
    works: {
      eyebrow: 'Ritual insignia',
      headlineLine1: 'The Works.',
      headlineLine2: 'Sin atajos.',
      ritualBody:
        'La hora completa. Corte, barba en forma, cejas, cera de nariz, y toalla caliente. El servicio del que hablan con sus panas.',
      priceLabel: 'Una hora en la silla',
      ctaLabel: 'Reserva The Works',
      imageCaption: 'El acabado importa',
      kicker: 'Servicio estrella · The signature service',
      headline: 'The Works — {worksPrice}',
      body:
        'Corte. Barba perfilada. Cejas arregladas. Cera para la nariz. Toalla caliente para terminar. Una hora en la silla, y sales pareciendo que te casas esa misma tarde. Por esto nos conocen.',
      cta: 'Reserva The Works →',
    },
    crew: {
      kicker: 'Meet the Barbers · Conoce a los Barberos',
      bookAny: 'Reserva cualquier silla →',
      book: 'Reserva →',
      bookWith: 'Reserva con {name} →',
      tenure: '{years} años',
      photoAlt: '{name}, {role} en {shopName}',
      ownerPhotoAlt: '{name}, dueño, cortándole el pelo a un cliente en {shopName}',
      nextAt: 'Próxima a las {time}',
      bookNow: 'Reserva ahora →',
      headline: 'Elige tu silla.',
      noPreference: '¿Sin preferencia? Te emparejamos con la próxima silla libre.',
      cta: 'Reserva en línea →',
      lines: {
        orlando: {
          short:
            'La silla más reservada de la barbería. Fades tan limpios que vas a querer mantener la forma por un mes.',
          bio:
            '{orlandoYears}+ años de experiencia. Se especializa en fades, trabajo de barba, y perfilados de precisión. La silla más reservada de la barbería por una razón.',
        },
        kenny: {
          short: 'Dueño. {kennyYears} años detrás de la silla. Pregúntale de lo que sea — cortes, barbas, o de la barbería misma.',
          bio: 'Dueño, {kennyYears}+ años detrás de la silla. Bilingüe (inglés/español). Si no estás seguro con quién reservar, pide a Kenny.',
        },
        rafy: {
          short: 'Precisión de la vieja escuela. {rafyYears} años de experiencia, y se nota cada vez.',
          bio:
            '{rafyYears} años de experiencia en barbería. Habilidades de la vieja escuela, ojo moderno. Los clientes fijos juran por sus fades y su trabajo con navaja.',
        },
        dylan: {
          short: 'Talento joven. Rápido, preciso, y totalmente al día con los estilos modernos.',
          bio:
            '{dylanYears}+ años en el oficio y una de las sillas que más rápido está creciendo en la barbería. Excelente para cortes modernos, diseños precisos, y perfilados limpios.',
        },
        emanuel: {
          short: 'El más nuevo del equipo. Ya está convirtiendo clientes nuevos en clientes fijos.',
          bio:
            'El más nuevo del equipo, pero no nuevo en el oficio. Está construyendo su cartera rápido — y convirtiendo clientes nuevos en clientes semanales.',
        },
      },
    },
    gallery: {
      eyebrow: 'El trabajo · The work',
      headline: 'Recién salido de la silla.',
      meta: 'Trae una foto o déjanoslo a nosotros — de cualquier forma sales impecable.',
      instagramCta: {
        full: 'Ve más en Instagram — @{instagramHandle}',
        short: 'Instagram — @{instagramHandle}',
      },
      eyebrowSimple: 'Gallery · Galería',
      labels: {
        'skin-fade-side-profile': 'Skin fade — vista lateral',
        'fade-natural-top': 'Fade — top natural',
        'kids-fade-curly-top': 'Fade infantil — rizos',
        'lineup-detail': 'Line-up — borde limpio',
      },
    },
    walkins: {
      eyebrow: 'Sin cita · Walk-ins',
      headline: '¿Sin cita? Igual bienvenido.',
      body: 'Hay una tablet junto a la puerta. Apunta tu nombre y ve por un café — te escribimos cuando tu silla esté cerca.',
      steps: [
        'Escribe tu nombre en la tablet junto a la puerta.',
        'Elige tu barbero, o toma el primero que se desocupe.',
        'Te escribimos cuando te toque — vuelve y siéntate.',
      ],
      ctaPrimary: 'Ponte en la fila',
      ctaSecondary: 'Llama ya · {shopPhone}',
      capeCaption: 'La silla de Kenny. Tu nombre sigue.',
      imageAlt: 'Una capa de {shopName} sobre una silla de barbero, esperando',
    },
    visit: {
      eyebrow: 'Visítanos · Visit',
      headline: '{addressLine1}, {city}',
      body: 'Justo en la esquina de {crossStreet}. Amplio estacionamiento al frente — vas a oír la barbería antes de verla.',
      hoursCard: {
        weekdays: 'Lunes a Viernes',
        saturday: 'Sábado',
        sunday: 'Domingo',
        sundayClosed: 'Cerrado — nos vemos el lunes',
      },
      ctaPrimary: 'Llama a la barbería',
      ctaSecondary: 'Cómo llegar',
      callUs: 'Llámanos',
      pinTitle: '{shopName} · {streetName}',
      pinSubtitle: 'En la State Road 54 · {plazaName} · estacionamiento al frente',
      mapAlt: 'Vista satelital de {shopName} en {streetName}, {city}',
      mapTitle: 'Mapa de {shopName}',
    },
    theSpace: {
      eyebrow: 'La Barbería · The Shop',
      headlineLine1: 'Más que cinco sillas',
      headlineLine2: 'y un buen fade.',
      body: 'La mesa de pool está lista. Los dominós en el mostrador. Snacks y bebidas frías en la máquina. Trae a los muchachos, tómate tu tiempo — el fade sale igual de fresco.',
      amenities: ['Mesa de pool', 'Dominó listo', 'Snacks y bebidas frías'],
    },
    menu: {
      eyebrow: 'La carta · The menu',
      headline: 'Desde {seniorFromPrice} el corte de mayor hasta {worksPrice} el completo.',
      subhead: 'Todo en la carta. Añade lo que necesites. El equipo de Kenny lo deja fresco.',
      featured: {
        label: 'El servicio insignia',
        description:
          'Corte, barba, toalla caliente, contorno, cejas. Una hora en la silla — vas a salir con la versión de ti que llega a hacer negocios.',
      },
      haircutsLabel: 'Haircuts / Recortes',
      addonsLabel: 'Añadidos',
      addonsSubhead: 'Agrega cualquier cosa al corte.',
      softCtaIntro: '¿No sabes qué necesitas?',
      softCtaLink: 'Llama a Kenny',
      worksSubline: 'Corte, barba, toalla caliente, contorno, cejas.',
      softCtaCall: 'Llámanos',
    },
    welcome: {
      est: 'Est. {foundedYear}',
      line1:
        'Ven y toma asiento — tenemos una silla esperándote. Conseguir un corte fresco nunca ha sido tan fácil.',
      line2: 'Reserva la hora que te convenga o simplemente entra. Sin cita, sin problema',
    },
    carousel: { prev: 'Anterior', next: 'Siguiente' },
    trustRibbon: {
      eyebrow: 'El estándar de la casa',
      items: ['Tu barbero, tú eliges', 'Fila de walk-in sin lío', 'Estacionamiento al frente'],
      cta: 'Empezar reserva',
    },
    sectionNumbers: {
      shopStory: '01',
      signature: '02',
      crew: '03',
      gallery: '04',
      walkins: '05',
    },
    trustStrip: {
      reviewsNoun: 'reseñas',
      averageLabel: 'promedio',
      source: 'en Booksy y Google',
      starsAlt: '{combinedRating} de 5 estrellas',
    },
    servicesTeaser: {
      kicker: 'El menú',
      headline: 'Desde cortes de {seniorFromPrice} hasta The Works de {worksPrice}.',
      haircutsLabel: 'Cortes',
      addOnsLabel: 'Extras',
      cta: 'Menú completo, precios, y cuánto toma cada uno en la página de servicios →',
    },
    reviews: {
      kicker: 'Lo que dice la gente',
      headline: '{combinedReviewTotal} reseñas. Promedio de {combinedRating} estrellas.',
      subline: 'Entre Booksy y Google. Clientes reales, cortes reales.',
      sublineGoogle: 'Clientes reales, cortes reales en Google.',
      errorFallbackHeadline: 'Lee lo que dice la gente',
      errorFallbackCta: 'Ver reseñas en Google',
      // Reseñas reales verificadas en Booksy, capturadas 2026-08-24.
      // Traducidas del inglés — el original de cada reseña está en copy.en.ts.
      reviewQuotes: [
        {
          quote: 'Satisfecho cada vez. Rápido y eficiente.',
          attribution: 'Elton · Ago 2026 · Booksy',
        },
        {
          quote:
            'Esta es una barbería excelente. No hay ni un mal barbero en el lugar — siempre sales satisfecho.',
          attribution: 'George · Ago 2026 · Booksy',
        },
        {
          quote: 'Kenny es el mejor. Siempre nos atiende a mí, a mi mamá y a mis hijos.',
          attribution: 'Tymira · Ago 2026 · Booksy',
        },
      ],
      ctaGoogle: 'Lee más en Google →',
      ctaBooksy: 'Booksy →',
    },
    location: {
      kicker: 'Ven a vernos',
      directionsCta: 'Cómo llegar →',
      contactLabel: 'Llama o manda texto:',
    },
    footerCta: {
      headline: 'Listos cuando tú lo estés.',
      subline: 'Reserva en línea, pásate, o llama. Lo que te sea más fácil.',
      ctaBook: 'Reserva en línea',
      ctaCall: 'Llama al {shopPhone}',
    },
  },

  about: {
    hero: {
      kicker: 'Sobre la barbería',
      headline: 'KD Barbershop abrió sus puertas en {foundedYear}. La misión no ha cambiado desde entonces.',
      subline: 'Grandes cortes. Sin actitud. Todos son bienvenidos.',
    },
    story: {
      headline: 'Cómo empezó KD',
      paragraphs: [
        'Kenny Diaz lleva {kennyYearsExperience} años cortando cabello. Trabajó en suficientes barberías como para saber lo que le gustaba y lo que no — y cómo quería que se sintiera su propia barbería cuando se abriera la puerta.',
        'En {foundedYear}, abrió KD Barbershop en {streetName}. El plan era simple: contratar barberos que se toman en serio el oficio, mantener precios justos, y asegurarse de que cada persona que entra — sea su primer corte aquí o su corte número cincuenta — se sienta como que pertenece.',
        'Cuatro años después, el equipo es de cinco. Entre todos, han hecho miles de cortes y ganado {combinedReviewTotal} reseñas entre Booksy y Google. No porque persigan reseñas. Porque el trabajo habla por sí solo.',
      ],
    },
    values: {
      headline: 'Tres cosas en las que no comprometemos.',
      craft: {
        label: 'Oficio',
        body:
          'Cada barbero aquí tiene experiencia real. No "empezó el mes pasado" experiencia — años detrás de la silla. Cuando te sientas, estás en manos de expertos.',
      },
      respect: {
        label: 'Respeto',
        body:
          'Esta barbería es para todos. Niños en su primer corte. Abuelos que vienen por un recorte. Chicos preparándose para su boda. Nadie recibe mejor o peor trato según lo que esté pagando.',
      },
      community: {
        label: 'Comunidad',
        body:
          'KD es una barbería de {city}. Kenny vive aquí, la mayoría del equipo vive aquí, y conocemos a nuestros clientes fijos por su nombre. Ese es el punto.',
      },
    },
    team: { headline: 'El equipo' },
    comeBy: {
      body: 'Estamos en {shopAddress}. Ven, siéntate, y compruébalo tú mismo.',
      ctaBook: 'Reserva en línea',
      ctaDirections: 'Cómo llegar',
    },
  },

  services: {
    hero: {
      kicker: 'El menú completo',
      headline: 'Cada corte. Cada extra. Cada precio.',
      subline: 'Sin tarifas ocultas, sin sorpresas. Lo que ves es lo que pagas.',
    },
    haircuts: { headline: 'Cortes' },
    addOns: {
      headline: 'Extras',
      intro:
        'Añádelos a cualquier corte. Solo dile a tu barbero cuando te sientes, o agrégalos al reservar.',
    },
    bookThis: 'Reservar',
    cta: {
      headline: '¿Listo para reservar?',
      body:
        'Elige tu barbero, elige tu servicio, y agarra tu hora. O pásate — aceptamos walk-ins cuando hay una silla libre.',
      ctaBook: 'Reserva en línea',
      ctaWalkIn: 'Ven como walk-in',
    },
    descriptions: {
      'mens-fade': {
        description:
          'Nuestro servicio más reservado. Fade en degradado, limpio, sales listo. Añade un perfilado de barba si quieres el look completo.',
      },
      'fade-beard': {
        description:
          'Fade más recorte y perfilado de barba. Este es el paquete "listo para todo." Popular entre los que tienen citas fijas los viernes.',
      },
      'fade-beard-eyebrows': {
        description:
          'Limpieza facial completa: fade, forma de barba, y cejas arregladas. Todo arriba de los hombros, bien hecho.',
      },
      'boys-under-12': {
        description:
          'Cortes para niños, hechos con paciencia. ¿Primer corte? Hemos hecho miles. Se van sonriendo.',
      },
      'senior-cut': {
        description: 'Precio con descuento para nuestros clientes mayores. Trae ID la primera vez.',
      },
      'senior-cut-beard': {
        description: 'Corte y barba al precio de la tercera edad. Trae ID la primera vez.',
      },
      'the-works': {
        description:
          'El insignia. Corte + perfilado de barba + cejas + cera para nariz + toalla caliente. Una hora en la silla, sales sintiéndote como un millón de dólares.',
      },
      'full-shape-beard': {
        description:
          'Perfilado completo de barba más shape up completo. Buen extra para cualquier corte.',
      },
      'hot-towel': {
        description:
          'Servicio clásico de toalla caliente. Ablanda la barba, limpia la piel, se siente increíble. Añádelo a cualquier servicio con barba.',
      },
      designs: {
        description:
          'Diseños libres, letras, o trabajo de líneas personalizado. El precio varía con la complejidad — habla con tu barbero.',
      },
      'shape-up': {
        description: '¿Entre cortes? Pásate solo por un perfilado. Bordes frescos en 10 minutos.',
      },
      eyebrows: { description: 'Arregla las cejas mientras estás en la silla.' },
      'nose-wax': {
        description: 'Rápido, sin dolor, y honestamente, más gente lo necesita de lo que admite.',
      },
    },
  },

  wizard: {
    panel: {
      header: 'Detrás del mostrador',
      liveLabel: 'Live',
      steps: [
        { title: 'Servicio seleccionado', meta: '{serviceName} · {price} · {duration} min' },
        { title: 'Agenda del barbero revisada', meta: '{barberName} · disponibilidad sincronizada' },
        { title: 'Silla reservada por 10 minutos', meta: 'Reservada hasta las {holdExpiry}' },
        { title: 'Contacto guardado y etiquetado', meta: 'source: website · tag: web-booking' },
        { title: 'SMS de confirmación enviado', meta: 'llega en unos 4 segundos, con la dirección' },
        { title: 'Recordatorios activados', meta: '{reminderPrimaryHours} horas antes, luego {reminderSecondaryHours} horas antes' },
        { title: 'Solicitud de reseña programada', meta: '3 horas después de tu servicio' },
        { title: 'Recordatorio de recorte en cola', meta: "día 26 · «¿Listo para un recorte?»" },
      ],
      anyBarberMeta: 'Cualquier barbero · escaneando todos',
      footer: 'Todo esto se hace solo — nadie en la barbería mueve un dedo',
    },
    step1: {
      headline: '¿Qué hacemos hoy?',
      subline: 'Elige tu servicio principal. Puedes agregar extras en un minuto.',
    },
    step2: {
      headline: '¿Quién corta?',
      subline: 'O te emparejamos con la próxima silla libre.',
      emptyState:
        '{barberFirstName} está completamente reservado los próximos 3 días. ¿Quieres ver disponibilidad de otro barbero, o elegir una fecha más tarde?',
      noPreference: 'Sin preferencia — próxima disponible',
      emptyStateHeadline: 'No hay barberos en el reloj ahora mismo.',
      emptyStateBody: 'El equipo de Kenny también reserva por Booksy — elige la hora que te sirva.',
      emptyStateBooksyCta: 'Reservar en Booksy',
    },
    step3: {
      headline: '¿Cuándo te queda bien?',
      subline: 'Los horarios que ves son las aperturas reales de tu barbero.',
      emptyState: 'No hay aperturas este día. Prueba mañana, o elige otro barbero.',
    },
    noAvailability: {
      fallbackIntro:
        '¿No hay nada disponible el día que elegiste? El equipo de Kenny también reserva por Booksy — puede que haya espacios ahí que no aparecen aquí.',
      booksyCta: 'Reservar en Booksy',
    },
    step4: {
      headline: '¿Algo más mientras estás aquí?',
      subline: 'Opcional. Sáltalo si solo vienes por lo principal.',
      runningTotal: 'Tu cita: {service} + {addOns} · {totalTime} · {totalPrice}',
      ctaContinue: 'Continuar →',
      ctaSkip: 'Saltar extras',
    },
    step5: {
      headline: 'Casi listo.',
      subline: 'Te vamos a mandar la confirmación y un recordatorio antes de tu cita por texto.',
      fields: {
        firstName: 'Nombre',
        lastName: 'Apellido',
        phone: 'Teléfono',
        email: 'Email (opcional)',
      },
      // §6.6 — legally operative TCPA language. Do not paraphrase.
      consentTransactional: {
        label: 'Recíbeme mensajes de confirmación y recordatorios de citas.',
        body:
          'Al marcar esta casilla, aceptas recibir SMS relacionados con tus citas de KD Barbershop. Pueden aplicar tarifas de mensajes y datos. Responde STOP para darte de baja. Responde HELP para ayuda.',
      },
      consentMarketing: {
        label: 'También recíbeme promociones y ofertas ocasionales.',
        body: 'Opcional. Las mismas reglas de STOP/HELP aplican.',
      },
      ctaConfirm: 'Confirmar reserva',
    },
    confirmation: {
      headline: '¡Estás reservado! 👑',
      withBarber: 'con {barberFirstName}',
      body:
        'Te vamos a mandar una confirmación ahora mismo, un recordatorio {reminderPrimaryHours} horas antes, y otro {reminderSecondaryHours} horas antes. Si surge algo, responde a cualquier texto o llama al {shopPhone}.',
      ctaCalendar: 'Agregar al calendario',
      ctaDirections: 'Cómo llegar',
    },
    errors: {
      slotTaken:
        'Esa hora acaba de ser reservada por alguien más. ¿Te tomas otros 30 segundos y eliges una hora diferente?',
      phoneLookupFailed:
        'Mmm — no encontramos ese número. Está bien, te vamos a crear un perfil nuevo cuando termines de reservar.',
      generic:
        'Algo no funcionó bien. Inténtalo otra vez, o llámanos al {shopPhone} y te reservamos directo.',
    },
  },

  walkInStatus: {
    upWith: 'Te toca con {barber}.',
    upSubline: 'Pasa a la silla.',
    inLineWith: 'Estás en fila con {barber}.',
    inLineSubline: 'Unos {minutes} min.',
    positionNext: 'Eres el próximo.',
    positionNth: 'Eres el #{position} en la fila.',
    nextBarber: 'el próximo barbero',
    willCall: 'Te llamamos por tu nombre.',
  },

  kioskWalkIn: {
    reset: {
      countdown: 'Volvemos al inicio en {seconds}',
      done: 'Listo',
      idleHeadline: '¿Sigues ahí?',
      idleBody: 'Empezamos de nuevo en {seconds} para que el próximo tenga la pantalla limpia.',
      idleStay: 'Sí, aquí estoy',
    },
    service: {
      headline: '¿Qué hacemos hoy?',
      subline: 'Elige tu servicio principal. Puedes agregar extras en un minuto.',
    },
    addOns: {
      headline: '¿Algo más mientras estás aquí?',
      subline: 'Opcional. Sáltalo si solo vienes por lo principal.',
      runningTotal: '{service} + {addOns} · {totalTime} · {totalPrice}',
      ctaContinue: 'Continuar →',
      ctaSkip: 'Saltar extras',
    },
    barber: {
      headline: '¿Quién quieres que te corte hoy?',
      nextAvailable: 'Próximo disponible',
      onTheClock: '{count} en turno',
      freeNow: 'Libre ahora',
      withClient: 'Con un cliente — puedes esperar',
      emptyHeadline: 'No hay barberos en turno ahora mismo.',
      emptySubline: 'Abrimos a las {nextPostedOpen}.',
    },
    contact: {
      headline: 'Una última cosa.',
      subline: 'Para encontrarte cuando se desocupe una silla.',
      nameLabel: 'Tu nombre',
      phoneLabel: 'Teléfono',
      ctaSubmit: 'Registrarme',
    },
    result: {
      assignedHeadline: 'Te toca con {barberFirstName}.',
      assignedSubline: 'Aproximadamente {duration} min.',
      busyHeadline: 'Todos los barberos están ocupados ahora mismo.',
      busyPreferred: '{barberFirstName} está con un cliente.',
      positionNext: 'Eres el siguiente.',
      positionNth: 'Eres el #{position} en la fila.',
      ctaWait: 'Espero',
      ctaLeave: 'Vuelvo más tarde',
      leftMessage: 'Vuelve cuando quieras.',
    },
    errors: {
      shopEmpty: 'No hay barberos en turno. Inténtalo más tarde.',
      duplicate: 'Ya estás registrado. Muéstrale esta pantalla a tu barbero: {shortId}',
      generic: 'El registro no funcionó. Inténtalo otra vez o pregunta en el mostrador.',
    },
    back: '← Atrás',
    waiting: {
      loading: 'Revisando la fila…',
      readyHeadline: '¡Te toca con {barberFirstName}!',
      readySubline: 'Regresa a la barbería.',
      inLineKicker: 'Estás en la fila',
      positionNext: 'Eres el siguiente',
      positionNth: '#{position}',
      waitingForBarber: 'Esperando a {barberFirstName}',
      willCall: 'Te llamamos por tu nombre cuando se desocupe una silla.',
      projected: 'Próximo disponible: {projectedBarber} en unos {projectedWaitMinutes} min.',
      doneHeadline: 'Tu corte está listo.',
      doneSubline: 'Gracias por venir.',
      releasedHeadline: 'Tu lugar fue liberado.',
      releasedSubline: 'Vuelve cuando quieras.',
      notFoundHeadline: 'No encontramos ese lugar en la fila.',
      notFoundSubline: 'Puede que haya expirado. Pasa al mostrador o llama al {shopPhone}.',
      scanHint: 'Escanea para verlo en tu teléfono',
      cancelCta: 'Cancelar mi lugar',
    },
  },

  mobile: {
    stickyBar: { book: 'Reserva', walkin: 'Sin cita', call: 'Llama', label: 'Acciones rápidas' },
  },

  footer: {
    directionsCta: 'Cómo llegar',
    followLabel: 'Síguenos',
    exploreLabel: 'Explora',
    booksyCta: 'Reserva en Booksy',
    signoff: 'Nos vemos pronto. © {currentYear} {shopName} · {city} {state}',
    legal: {
      copyright: '© {currentYear} {shopName}',
      privacy: 'Política de Privacidad',
      terms: 'Términos de Servicio',
      smsTerms: 'Términos SMS',
      builtBy: 'Sitio por SmartCity Contractors',
    },
  },
} satisfies CopyDict;

export default es;
